<template>
  <div class="map-container" style="position: relative">
    <StopsComponent
      v-if="
        this.consigneeAddress &&
        this.isBelowSmallestBreakpoint &&
        !configGetter(this.$rttI18n.tenantId, this.themeId).disableOverview
      "
      :stops="this.myTrackingData.status.remainingStops"
      :top="true"
    />
    <PostCodeInputComponent />
    <v-card v-if="!this.consigneeAddress" variant="flat">
      <img
        alt="Static Map"
        class="staticmap_fallback v-col-12 pa-0 d-block"
        src="/staticmap_550x450.jpg"
      />
    </v-card>
    <AwsMap
      v-if="this.consigneeAddress"
      :center="position"
      :class="wrapperClass"
      :options="mapOptions"
      :zoom="15"
      class="map-wrapper v-col-12 pa-0"
      v-on:initialized="awsMapInitialized"
    />
    <StopsComponent
      v-if="
        this.consigneeAddress &&
        (!isBelowSmallestBreakpoint ||
          configGetter(this.$rttI18n.tenantId, this.themeId).disableOverview)
      "
      :stops="this.myTrackingData.status.remainingStops"
      :top="false"
    />
    <p
      v-if="showPositionExplanation"
      :class="[
        'text-footnote ml-1 mt-2',
        { 'position-explanation-half-width': showHalfWidthPositionExplanation }
      ]"
    >
      {{
        $rttI18n.tenantSpecificWithFallback(
          'positionExplanation',
          'stops.positionExplanation',
          {
            stops: this.myTrackingData.maxStopsToShowDriverLocation
          }
        )
      }}
    </p>
  </div>
</template>

<script>
import { mapActions, mapState } from 'pinia';
import PostCodeInputComponent from '../PostCodeInputComponent';
import StopsComponent from '../StopsComponent';
import {
  TRACKING_DELIVERY_STATE_FAR,
  TRACKING_DELIVERY_STATE_NEAR,
  TRACKING_STATE_DETAIL_PARCEL_SHOP
} from '@/stores/constants.js';
import AwsMap from '@/components/map/AwsMap';
import { LngLat, LngLatBounds, Marker } from 'maplibre-gl';
import { sendAnalytics } from '@/services/analytics';
import publicPath from '@/util/publicPath';
import { configGetter } from '@/config/configGetter';
import dialogs from '@/components/dialogs/dialogs.js';
import { toRaw } from 'vue';
import { useDialogStore } from '@/stores/dialog.js';
import { useResponsiveBreakpointsStore } from '@/stores/responsiveBreakpoints.js';
import { useThemeStore } from '@/stores/theme.js';
import { useTrackingDataStore } from '@/stores/trackingData.js';
import arraysEqual from '@/util/arraysEqual.js';

export default {
  name: 'AwsMapComponent',
  components: {
    AwsMap,
    PostCodeInputComponent,
    StopsComponent
  },
  props: {
    showLiveMap: {
      type: Boolean,
      default: false
    },
    trackingId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      map: null,
      dotAnimationInterval: null,
      previousRoutePath: null,
      iconDotMarker: null,
      addressMarker: null,
      TRACKING_DELIVERY_STATE_NEAR,
      TRACKING_DELIVERY_STATE_FAR
    };
  },
  computed: {
    ...mapState(useResponsiveBreakpointsStore, ['isBelowSmallestBreakpoint']),
    ...mapState(useThemeStore, ['themeId']),
    ...mapState(useTrackingDataStore, {
      myTrackingData: 'data',
      getTrackingState: 'getTrackingState',
      showPositionExplanation: 'showPositionExplanation',
      isServiceTypePickup: 'isServiceTypePickup',
      getDeliveryStatusDetail: 'getDeliveryStatusDetail',
      consigneeAddress: 'consigneeAddress'
    }),
    isParcelShopDelivery() {
      return this.getDeliveryStatusDetail === TRACKING_STATE_DETAIL_PARCEL_SHOP;
    },
    mapOptions() {
      return {
        maxZoom: 16,
        interactive: this.showLiveMap,
        maxBounds: LngLatBounds.fromLngLat(
          new LngLat(this.position.lng, this.position.lat),
          100000
        )
      };
    },
    position() {
      return (
        this.myTrackingData.status.address?.coordinates ||
        this.deliveredToParcelShopLocation ||
        this.consigneeCoordinates
      );
    },
    consigneeCoordinates() {
      return this.consigneeAddress?.coordinates;
    },
    wrapperClass() {
      return configGetter(this.$rttI18n.tenantId, this.themeId).disableOverview
        ? 'overview-disabled'
        : '';
    },
    showHalfWidthPositionExplanation() {
      return !(
        this.isBelowSmallestBreakpoint ||
        this.getTrackingState === TRACKING_DELIVERY_STATE_FAR ||
        configGetter(this.$rttI18n.tenantId, this.themeId).disableStopsComponent
      );
    },
    deliveredToParcelShop() {
      return this.myTrackingData.nearParcelShops?.find(
        it =>
          (this.myTrackingData.status.address &&
            this.stringCompare(
              it.name,
              this.myTrackingData.status.address.name
            )) ||
          this.addressEqualsParcelShop(this.consigneeAddress, it) ||
          this.addressEqualsParcelShop(
            this.myTrackingData.status.address,
            it,
            this.isParcelShopDelivery
          )
      );
    },
    deliveredToParcelShopLocation() {
      const address = this.deliveredToParcelShop?.address;
      return (
        address && {
          lng: address.longitude,
          lat: address.latitude
        }
      );
    }
  },
  methods: {
    configGetter,
    ...mapActions(useDialogStore, ['toggleDialog']),
    marker({ element, position }) {
      return new Marker({ element }).setLngLat(position).addTo(this.map);
    },
    createMapMarkerElement(icon, label, tabIndex) {
      const el = document.createElement('div');
      el.className = 'marker';
      el.style.backgroundImage = `url(${icon.image})`;
      el.style.backgroundSize = 'contain';
      el.style.width = icon.width + 'px';
      el.style.height = icon.height + 'px';
      el.style.zIndex = icon.zIndex || '1';
      el.setAttribute('aria-label', label);
      if (tabIndex >= 0) {
        el.tabIndex = tabIndex;
        el.role = 'button';
      }

      return el;
    },
    addressEqualsParcelShop(address, ps, useCoordinateMatching = false) {
      const addressMatch =
        this.stringCompare(address?.name, ps.name) &&
        this.stringCompare(address?.street, ps.address.street) &&
        this.stringCompare(address?.number, ps.address.houseNumber) &&
        this.stringCompare(address?.postcode, ps.address.zipCode) &&
        this.stringCompare(address?.city, ps.address.city);

      const coordinateMatch =
        useCoordinateMatching &&
        address?.coordinates &&
        this.stringCompare(
          LngLat.convert(address?.coordinates).toString(),
          this.parcelShopToLngLat(ps).toString()
        );

      return addressMatch || coordinateMatch;
    },
    createMapMarker(position, icon, label, tabIndex) {
      return this.marker({
        element: this.createMapMarkerElement(icon, label, tabIndex),
        position
      });
    },
    dialogHandler(dialog, payload) {
      this.toggleDialog({ dialog, payload });
    },
    createParcelShopMarker(parcelShop, icon) {
      const openDialog = () => {
        sendAnalytics(
          this.myTrackingData,
          'map',
          'parcel_shop_marker_clicked',
          {
            parcelShopId: parcelShop.parcelShopId,
            distance: parcelShop.distance,
            type: parcelShop.type,
            name: parcelShop.name
          }
        );
        this.dialogHandler(dialogs.PARCEL_SHOP, parcelShop);
      };
      let el = this.createMapMarkerElement(icon, parcelShop.name, 0);
      el.style.cursor = 'pointer';
      el.onclick = openDialog;
      el.onkeydown = e => {
        // Allow enter and whitespace as trigger
        if (e.code !== 'Enter' && e.code !== 'Space') return;
        openDialog();
      };

      return this.marker({
        element: el,
        position: new LngLat(
          parcelShop.address.longitude,
          parcelShop.address.latitude
        )
      });
    },
    stringCompare(s1, s2) {
      return this.comparable(s1) === this.comparable(s2);
    },
    comparable(s) {
      return this.withoutWhitespace(String(s)).toLowerCase();
    },
    withoutWhitespace(s) {
      return s.replace(/\s+/g, '');
    },
    parcelShopToLngLat(parcelShop) {
      return new LngLat(
        parcelShop.address.longitude,
        parcelShop.address.latitude
      );
    },
    animateRoute(geoJson) {
      const iconDot = publicPath(
        `/themes/${this.themeId}/map/icon_route_dot.svg`
      );
      const routePath = this.getRouteCoordinates(geoJson);

      if (!this.previousRoutePath) {
        this.previousRoutePath = routePath;
      } else {
        if (arraysEqual(routePath, this.previousRoutePath)) {
          return;
        }
        if (this.dotAnimationInterval) {
          clearInterval(this.dotAnimationInterval);
        }
        if (this.iconDotMarker) {
          this.iconDotMarker.remove();
        }
      }

      const routeLegsLength = routePath?.length - 2;
      if (
        routeLegsLength > 1 &&
        this.myTrackingData.status.remainingStops > 0
      ) {
        const stopFactor = Math.round(
          routeLegsLength / this.myTrackingData.status.remainingStops
        );
        let intervalIteration = 1;

        this.iconDotMarker = this.createMapMarker(
          routePath[1],
          {
            image: iconDot,
            width: 8,
            height: 8,
            zIndex: '0'
          },
          this.$t('map.remainingStopsMarker')
        );

        this.dotAnimationInterval = setInterval(() => {
          const leg = stopFactor * intervalIteration;
          if (leg < routeLegsLength) {
            const routeLeg = routePath[leg];
            this.iconDotMarker.setLngLat(routeLeg);
            intervalIteration++;
          } else {
            intervalIteration = 1;
          }
        }, 1000);
      }
    },
    getRouteCoordinates(routeGeoJson) {
      return routeGeoJson.geometry?.coordinates;
    },
    getRoute() {
      return this.myTrackingData.routeGeoJson;
    },
    removeRoute() {
      if (this.map && this.map.getLayer('route')) {
        this.map.removeLayer('route');
        this.map.removeSource('route');
      }
    },
    executeOnMap(callback) {
      if (!this.map) {
        // eslint-disable-next-line no-console
        console.error('Map not initialized yet.');
      } else if (this.map._loaded) {
        callback();
      } else {
        this.map.on('load', callback);
      }
    },
    setMapSourceData(map, sourceName, sourceData) {
      if (map.getSource(sourceName)) {
        map.getSource(sourceName).setData(sourceData);
      } else {
        map.addSource(sourceName, { type: 'geojson', data: sourceData });
      }
    },
    addNewMapLayer(map, layer) {
      if (!map.getLayer(layer.id)) map.addLayer(layer);
    },
    showStartRoute(lineColor) {
      const geoJson = this.getRoute();
      const trackingDataStore = useTrackingDataStore();

      const routeStartGeoJson = {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [
            [
              trackingDataStore.data.status.position.lng,
              trackingDataStore.data.status.position.lat
            ],
            geoJson.geometry.coordinates[0]
          ]
        }
      };
      this.setMapSourceData(this.map, 'route_start', routeStartGeoJson);
      this.addNewMapLayer(this.map, {
        id: 'route_start',
        type: 'line',
        source: 'route_start',
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': lineColor,
          'line-width': 5,
          'line-dasharray': [0.2, 1.5]
        }
      });
    },
    showEndRoute(lineColor) {
      const geoJson = this.getRoute();

      const routeEndGeoJson = {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [
            [this.consigneeCoordinates.lng, this.consigneeCoordinates.lat],
            geoJson.geometry.coordinates[
              geoJson.geometry.coordinates.length - 1
            ]
          ]
        }
      };
      this.setMapSourceData(this.map, 'route_end', routeEndGeoJson);
      this.addNewMapLayer(this.map, {
        id: 'route_end',
        type: 'line',
        source: 'route_end',
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': lineColor,
          'line-width': 5,
          'line-dasharray': [0.2, 1.5]
        }
      });
    },
    showRoute() {
      const routeColor = {
        bvb: 'rgba(0,0,0,0.35)',
        juskys: 'rgba(147, 192, 32, 0.5)',
        skandic: 'rgba(205, 47, 42, 0.5)',
        hmsk: 'rgba(77, 169, 225, 0.5)',
        theissen: 'rgba(83, 188, 133, 0.5)',
        rotWeissEssen: 'rgba(227, 6, 27, 0.5)',
        gentor: 'rgba(227, 6, 25, 0.5)',
        'royalmail-uk': 'rgba(230, 51, 56, 1)'
      };

      this.showParcelMarker();
      const geoJson = this.getRoute();

      if (geoJson?.bbox) {
        this.map.fitBounds(this.getBounds().extend(toRaw(geoJson.bbox)), {
          padding: 60,
          animate: false
        });
      }

      const lineColor = routeColor[this.themeId] ?? 'rgba(6,26,177,0.4)';

      this.executeOnMap(() => {
        this.setMapSourceData(this.map, 'route', geoJson);
        this.addNewMapLayer(this.map, {
          id: 'route',
          type: 'line',
          source: 'route',
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': lineColor,
            'line-width': 5
          }
        });
        this.showStartRoute(lineColor);
        this.showEndRoute(lineColor);
      });
      this.animateRoute(geoJson);
    },
    getBounds() {
      let bounds = LngLatBounds.fromLngLat(LngLat.convert(this.position), 0);

      if (this.getParcelLocation())
        bounds = bounds.extend(LngLat.convert(this.getParcelLocation()));

      const sortedShops = this.myTrackingData.nearParcelShops?.toSorted(
        (a, b) => a.distance - b.distance
      );
      if (sortedShops && !this.getRoute()) {
        // only extend to the closest 5 parcel shops
        for (let i = 0; i < 5; i++) {
          if (sortedShops[i]) {
            bounds = bounds.extend(this.parcelShopToLngLat(sortedShops[i]));
          }
        }
      }

      return bounds;
    },
    fitToBounds() {
      this.map.fitBounds(this.getBounds().toArray(), {
        padding: 60,
        animate: false
      });

      if (this.myTrackingData.nearParcelShops) {
        this.map.jumpTo({
          center: this.position,
          zoom: this.map.getZoom()
        });
      }
    },
    showParcelMarker() {
      const mapMarkerParcel = publicPath(
        `/themes/${this.themeId}/map/icon_parcel_on_delivery.svg`
      );
      const mapMarkerPickup = publicPath(
        `/themes/${this.themeId}/map/icon_pickup.svg`
      );

      if (this.parcelPositionMarker) this.parcelPositionMarker.remove();
      this.parcelPositionMarker = this.createMapMarker(
        this.myTrackingData.status.position,
        {
          height: 60,
          image: this.isServiceTypePickup ? mapMarkerPickup : mapMarkerParcel,
          width: 60
        },
        this.$t('map.parcelMarker')
      );

      this.parcelPositionMarker.addEventListener?.('click', () => {
        sendAnalytics(
          this.myTrackingData,
          'clickStats',
          'parcel_position_marker_clicked'
        );
      });
    },
    showParcelShops() {
      if (this.parcelShopMarkers) {
        this.parcelShopMarkers?.forEach(m => m.remove());
      }

      this.parcelShopMarkers = this.myTrackingData.nearParcelShops?.map(
        parcelShop => {
          return this.createParcelShopMarker(
            parcelShop,
            this.deliveredToParcelShop?.name === parcelShop.name
              ? {
                  height: 35,
                  image: publicPath(
                    `/themes/${this.themeId}/map/icon_gls_parcelshop1.svg`
                  ),
                  width: 35
                }
              : {
                  height: 30,
                  image: publicPath(
                    `/themes/${this.themeId}/map/icon_gls_parcelshop2.svg`
                  ),
                  width: 30
                }
          );
        }
      );
    },
    showAddressLocation() {
      const mapMarkerDestination = publicPath(
        `/themes/${this.themeId}/map/icon_destination.svg`
      );

      if (this.addressMarker) {
        this.addressMarker.remove();
      }

      this.addressMarker = this.createMapMarker(
        this.consigneeCoordinates,
        {
          height: 60,
          image: mapMarkerDestination,
          width: 60,
          zIndex: '10'
        },
        this.$t('generic.consignee')
      );

      this.addressMarker.addEventListener?.('click', () => {
        sendAnalytics(
          this.myTrackingData,
          'clickStats',
          `destination_marker_clicked`
        );
      });
    },
    showParcelShopMarker() {
      const mapMarkerParcelShop = publicPath(
        `/themes/${this.themeId}/map/icon_gls_parcelshop.svg`
      );

      if (this.statusAddressMarker) this.statusAddressMarker.remove();
      this.statusAddressMarker = this.createMapMarker(
        this.getParcelShopLocation(),
        {
          height: 60,
          image: mapMarkerParcelShop,
          width: 60
        },
        this.$t('map.parcelShopMarker')
      );
    },
    getParcelLocation() {
      return (
        this.myTrackingData.status.address?.coordinates ||
        this.myTrackingData.status.position ||
        this.deliveredToParcelShopLocation
      );
    },
    getParcelShopLocation() {
      return (
        this.myTrackingData.status.address?.coordinates ||
        this.deliveredToParcelShopLocation
      );
    },
    addMapAnalytics() {
      this.currentZoomLevel = 0;
      this.map.on('zoomstart', () => {
        this.currentZoomLevel = this.map.getZoom();
      });
      this.map.on('zoomend', event => {
        // Only send a zoom analytics event when the action is user‑initiated and not from a resize-triggered fitBounds calculation.
        // MapLibre sets `event.originalEvent` for Mouse/Touch/Wheel input; it is undefined for programmatic zooms like fitBounds.
        // See docs: https://maplibre.org/maplibre-gl-js/docs/API/type-aliases/MapEventType/#zoomend
        if (event?.originalEvent) {
          sendAnalytics(
            this.myTrackingData,
            'map',
            this.map.getZoom() > this.currentZoomLevel
              ? 'map_zoomed_in'
              : 'map_zoomed_out'
          );
        }
        this.currentZoomLevel = this.map.getZoom();
      });

      this.map.on('dragend', () => {
        sendAnalytics(this.myTrackingData, 'map', 'map_dragged');
      });
    },
    mapInit() {
      this.map.on('load', () => {
        // AWS v2 styles do not always include this legacy layer.
        if (this.map.getLayer('Spot elevation')) {
          this.map.removeLayer('Spot elevation'); // do not show elevation in meters on map
        }
      });
      if (!this.deliveredToParcelShop) {
        this.showAddressLocation();
      }
      this.showParcelShops();
      this.fitToBounds();

      if (this.getRoute()) {
        this.showRoute();
      } else if (
        this.isParcelShopDelivery &&
        !!this.getParcelShopLocation() &&
        !this.deliveredToParcelShopLocation
      ) {
        this.showParcelShopMarker();
      }

      this.addMapAnalytics();
    },
    awsMapInitialized(map) {
      this.map = map;
      this.mapInit();
    }
  },
  mounted() {
    const trackingDataStore = useTrackingDataStore();
    const themeStore = useThemeStore();

    themeStore.$subscribe((mutation, state) => {
      this.mapInit();
    });

    this.unwatchRemainingStops = this.$watch(
      () => trackingDataStore.data.status.remainingStops,
      () => {
        if (!this.getRoute()) {
          this.removeRoute();
        }
      }
    );

    this.unwatchPosition = this.$watch(
      () => trackingDataStore.data.status.position,
      (newPosition, oldPosition) => {
        const distanceCheck =
          !oldPosition ||
          (newPosition &&
            LngLat.convert(newPosition).distanceTo(oldPosition) > 0);
        if (
          this.map &&
          this.getRoute() &&
          this.getParcelLocation() &&
          distanceCheck
        ) {
          this.showRoute();
        }
      }
    );

    this.unwatchToAddressCoordinates = this.$watch(
      () => this.consigneeCoordinates,
      (newPosition, oldPosition) => {
        const distanceCheck =
          !oldPosition ||
          (newPosition &&
            LngLat.convert(newPosition).distanceTo(oldPosition) > 0);
        if (this.map && distanceCheck) {
          this.showAddressLocation();
        }
      }
    );

    this.unwatchStatusAddress = this.$watch(
      () => trackingDataStore.data.status.address,
      () => {
        if (this.map && !!this.getParcelShopLocation()) {
          this.fitToBounds();
          if (!this.deliveredToParcelShopLocation) {
            this.showParcelShopMarker();
          }
        }
      }
    );
  },
  beforeUnmount() {
    clearInterval(this.dotAnimationInterval);

    this.unwatchRemainingStops();
    this.unwatchPosition();
    this.unwatchToAddressCoordinates();
    this.unwatchStatusAddress();
  }
};
</script>

<style scoped>
.map-container {
  margin-bottom: 8px;
}

@media (min-width: 960px) {
  .map-container:has(> :nth-child(1)) {
    margin-bottom: 24px;
  }

  .map-container:has(> :nth-child(2)) {
    margin-bottom: 116px;
  }
}

.map-wrapper {
  height: 40vh;
  position: relative;
  border-radius: var(--border-radius-root);
  overflow: hidden;
}

.staticmap_fallback {
  min-height: 300px;
}

.position-explanation-half-width {
  width: 50%;
  padding-right: calc(66px + 0.2em);
  float: left;
}

@media (min-width: 1264px) {
  .map-wrapper {
    height: 60vh;
  }
}

@media (min-width: 960px) {
  .map-wrapper {
    height: 60vh;
  }
}

.overview-disabled {
  height: calc(100vh - 80px) !important; /* see StopsComponent height: 80px; */
}
</style>
